import React, { useState, useMemo } from "react";
import { ChevronRight, ArrowDownAZ, ArrowUpDown } from "lucide-react";
import { fmtINR } from "../utils";

const PLURALS = {
  category: { one: "category", many: "categories" },
  state: { one: "state", many: "states" },
  city: { one: "city", many: "cities" },
  store: { one: "store", many: "stores" },
};
function pluralize(groupKey, n) {
  const p = PLURALS[groupKey] || { one: groupKey, many: groupKey + "s" };
  return n === 1 ? p.one : p.many;
}

// Generic grouped grid — handles Category / State / City / Store levels.
// Each row card has the group label + two pill buttons (Sales, Purchase).
// Click either pill or the card body to drill into the next level.
//
// For the Store level (groupKey === 'store') the card adds a few extra
// meta-tags (category, city, state) so the cashier can identify the
// outlet without drilling further.
export default function GroupGrid({ groupKey, rows, onDrill, onOpenInvoices }) {
  const [sortBy, setSortBy] = useState("combined"); // combined | sales | purchase | name
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const out = rows.slice();
    out.sort((a, b) => {
      let av, bv;
      if (sortBy === "sales") {
        av = a.sales; bv = b.sales;
      } else if (sortBy === "purchase") {
        av = a.purchase; bv = b.purchase;
      } else if (sortBy === "name") {
        av = String(a.label || a.key).toLowerCase();
        bv = String(b.label || b.key).toLowerCase();
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      } else {
        av = a.sales + a.purchase;
        bv = b.sales + b.purchase;
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return out;
  }, [rows, sortBy, sortDir]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="text-[12px] text-[var(--dashboard-gray-500)]">
          {rows.length} {pluralize(groupKey, rows.length)}
        </div>
        <div className="flex items-center gap-1 text-[11.5px]">
          <span className="text-[var(--dashboard-gray-400)] mr-1">Sort by</span>
          {[
            { k: "combined", label: "Total" },
            { k: "sales", label: "Sales" },
            { k: "purchase", label: "Purchase" },
            { k: "name", label: "Name" },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => {
                if (sortBy === opt.k) {
                  setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                } else {
                  setSortBy(opt.k);
                  setSortDir(opt.k === "name" ? "asc" : "desc");
                }
              }}
              className={`px-2 py-1 rounded font-semibold flex items-center gap-1 ${
                sortBy === opt.k
                  ? "bg-[var(--dashboard-gray-900)] text-white"
                  : "text-[var(--dashboard-gray-500)] hover:bg-[var(--nav-head-bg)]"
              }`}
            >
              {opt.label}
              {sortBy === opt.k &&
                (opt.k === "name" ? (
                  <ArrowDownAZ size={11} className={sortDir === "desc" ? "rotate-180" : ""} />
                ) : (
                  <ArrowUpDown size={11} />
                ))}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sorted.map((r) => (
          <GroupCard
            key={r.key}
            row={r}
            groupKey={groupKey}
            onDrill={() => onDrill(r)}
            onOpenInvoices={onOpenInvoices ? (metric) => onOpenInvoices(r, metric) : null}
          />
        ))}
      </div>
    </div>
  );
}

function GroupCard({ row, groupKey, onDrill, onOpenInvoices }) {
  const isStore = groupKey === "store";
  const title = isStore ? row.code || row.key : row.label || row.key;
  const sub = isStore
    ? [row.city, row.state].filter(Boolean).join(" · ")
    : `${row.store_count} store${row.store_count === 1 ? "" : "s"}`;

  return (
    <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onDrill}
        className="w-full text-left px-4 pt-3.5 pb-2 hover:bg-[var(--dashboard-bg-table-head)] transition"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-bold text-[var(--dashboard-gray-900)] leading-tight truncate">
              {title}
            </div>
            <div className="text-[11.5px] text-[var(--dashboard-gray-500)] mt-0.5 truncate">{sub}</div>
          </div>
          {isStore && row.category ? (
            <span className="text-[10px] font-semibold text-[var(--dashboard-gray-500)] bg-[var(--dashboard-gray-100)] rounded-full px-2 py-0.5 whitespace-nowrap">
              {row.category}
            </span>
          ) : (
            <span className="text-[10px] font-semibold text-[var(--dashboard-gray-500)] bg-[var(--dashboard-gray-100)] rounded-full px-2 py-0.5 whitespace-nowrap">
              {row.store_count} store{row.store_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </button>
      <div className="px-4 pb-3 pt-1 flex gap-2">
        <MetricPill
          kind="sales"
          amount={row.sales}
          count={row.sales_count}
          returnsCount={row.sales_returns_count}
          onClick={
            isStore && onOpenInvoices
              ? (e) => { e.stopPropagation(); onOpenInvoices("sales"); }
              : (e) => { e.stopPropagation(); onDrill(); }
          }
        />
        <MetricPill
          kind="purchase"
          amount={row.purchase}
          count={row.purchase_count}
          onClick={
            isStore && onOpenInvoices
              ? (e) => { e.stopPropagation(); onOpenInvoices("purchase"); }
              : (e) => { e.stopPropagation(); onDrill(); }
          }
        />
      </div>
    </div>
  );
}

function MetricPill({ kind, amount, count, returnsCount, onClick }) {
  const tone =
    kind === "sales"
      ? "border-[var(--dashboard-bg-success)] text-[var(--dashboard-green-dark)] bg-[var(--dashboard-bg-success)] hover:bg-[var(--dashboard-bg-success)]"
      : "border-[var(--dashboard-bg-warning)] text-[var(--dashboard-warning-dark)] bg-[var(--dashboard-bg-warning)] hover:bg-[var(--dashboard-bg-warning)]";
  // Sales pill subtitle: "12 bills − 1 ret" if any returns happened, else "12 bills".
  // Purchase pill subtitle: "5 GRNs" (purchase metric is now goods received).
  const subtitle =
    kind === "sales"
      ? `${count} bill${count === 1 ? "" : "s"}${returnsCount ? ` − ${returnsCount} ret` : ""}`
      : `${count} GRN${count === 1 ? "" : "s"}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 border rounded-lg px-3 py-2 text-left ${tone} transition`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wide flex items-center justify-between">
        <span>{kind === "sales" ? "Sales (net)" : "Purchase / GRN"}</span>
        <ChevronRight size={11} />
      </div>
      <div className="font-mono text-[14.5px] font-bold mt-1">{fmtINR(amount)}</div>
      <div className="text-[10.5px] opacity-70 font-mono mt-0.5">{subtitle}</div>
    </button>
  );
}
